import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Compass, BookOpen, PlayCircle, Sparkles, Film, Video, Library,
  ClipboardCheck, FileText, TerminalSquare, MessageSquare, Mic, Flame, Users,
  Brain, Target, Trophy, BarChart3, ShieldCheck, BriefcaseBusiness, Briefcase,
  WalletCards, Gift, Bell, Calendar, User, HelpCircle
} from "lucide-react";
import { Toaster } from "react-hot-toast";

const navItems = [
  { divider: true, label: "CORE" },
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, route: "/dashboard" },
  { key: "purchase-courses", label: "Purchase Courses", icon: Compass, route: "/purchase-courses" },
  { key: "my-courses", label: "My Courses", icon: BookOpen, route: "/dashboard?tab=my-courses" },
  { key: "continue", label: "Continue Learning", icon: PlayCircle, route: "/dashboard?tab=continue" },

  { divider: true, label: "LEARNING" },
  { key: "ai-tutor", label: "AI Tutor", icon: Sparkles, route: "/ai-tutor" },
  { key: "recordings", label: "Recorded Videos", icon: Film, route: "/recordings" },
  { key: "live", label: "Live Classes", icon: Video, route: "/live-classes" },
  { key: "library", label: "Notes & Resources", icon: Library, route: "/library" },

  { divider: true, label: "EVALUATION" },
  { key: "exams", label: "Online Exams", icon: ClipboardCheck, route: "/exams" },
  { key: "subjective", label: "Descriptive Exams", icon: FileText, route: "/student-subjective-exams" },
  { key: "assignments", label: "Homework & Tasks", icon: FileText, route: "/assignments" },
  { key: "coding", label: "Coding Arena", icon: TerminalSquare, route: "/coding" },
  { key: "doubts", label: "Ask Doubts", icon: MessageSquare, route: "/doubts" },

  { divider: true, label: "GAMIFICATION" },
  { key: "voice", label: "Voice Tutor", icon: Mic, route: "/voice-tutor" },
  { key: "challenges", label: "Daily Challenge", icon: Flame, route: "/daily-challenges" },
  { key: "friend-compete", label: "Friend Compete", icon: Users, route: "/friend-competition" },
  { key: "revision", label: "Smart Revision", icon: Brain, route: "/ai-revision" },

  { divider: true, label: "ANALYTICS" },
  { key: "study-plan", label: "Study Plan", icon: Target, route: "/study-plan" },
  { key: "performance", label: "Performance", icon: Trophy, route: "/my-results" },
  { key: "leaderboard", label: "Leaderboard", icon: BarChart3, route: "/friend-competition?tab=leaderboard" },
  { key: "certificates", label: "Certificates", icon: ShieldCheck, route: "/my-results?tab=certificates" },

  { divider: true, label: "CAREERS" },
  { key: "placement", label: "Placement Preparation", icon: BriefcaseBusiness, route: "/placement-preparation" },
  { key: "internships", label: "Internships & Jobs", icon: Briefcase, route: "/placement-preparation?tab=jobs" },

  { divider: true, label: "COMMERCE" },
  { key: "subscription", label: "Subscription Plans", icon: WalletCards, route: "/subscription-plans" },
  { key: "referral", label: "Referral & Affiliate", icon: Gift, route: "/referrals" },

  { divider: true, label: "UTILITIES" },
  { key: "notifications", label: "Notifications", icon: Bell, route: "/dashboard?tab=notifications" },

  { divider: true, label: "ACCOUNT" },
  { key: "profile", label: "Profile & Settings", icon: User, route: "/student-profile" },
  { key: "help", label: "Help & Support", icon: HelpCircle, route: "/doubts?tab=support" },
];

export default function StudentLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentFullPath = location.pathname + location.search;

  const handleNavClick = (item) => {
    if (item.route) navigate(item.route);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-['Inter',sans-serif]">
      <Toaster position="top-center" />

      <div className="flex flex-col lg:flex-row">
        {/* ═══ Inner Sidebar Nav — Desktop ═══ */}
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-100 dark:border-slate-800/80 min-h-[calc(100vh-64px)] sticky top-16 overflow-y-auto py-4 px-3 bg-white/40 dark:bg-slate-950/40 backdrop-blur-sm">
          <nav className="space-y-1">
            {navItems.map((item, idx) => {
              if (item.divider) {
                return (
                  <div key={`div-${idx}`} className="pt-5 pb-2 px-3">
                    <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400/70">{item.label}</p>
                  </div>
                );
              }
              const Icon = item.icon;
              const isActive = currentFullPath === item.route;
              return (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  className={`group relative w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[13px] font-bold transition-all ${
                    isActive
                      ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/20"
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900/60 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  <Icon size={16} className={isActive ? "" : "group-hover:scale-110 transition-transform"} />
                  <span className="truncate">{item.label}</span>
                  {isActive && (
                    <motion.div layoutId="student-nav-pill" className="ml-auto w-1.5 h-1.5 rounded-full bg-white" />
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* ═══ Mobile Horizontal Tabs ═══ */}
        <div className="lg:hidden overflow-x-auto border-b border-slate-100 dark:border-slate-800/80 bg-white/60 dark:bg-slate-950/60 backdrop-blur-sm sticky top-16 z-10 w-full no-scrollbar">
          <div className="flex gap-1 p-2 min-w-max">
            {navItems.filter(n => !n.divider).map(item => {
              const Icon = item.icon;
              const isActive = currentFullPath === item.route;
              return (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-indigo-600 text-white shadow-md"
                      : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  <Icon size={14} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ═══ Main Content Area ═══ */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
